import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  FileText,
  TrendingUp,
  DollarSign,
  Calendar,
  ExternalLink,
  CheckCircle2,
  Clock,
  Eye,
  XCircle,
  Building2,
  Users,
  Filter,
  Sparkles,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { mockClientsService as clientsService } from '@/services/api/mock-clients-data';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { RegulatoryChange, ClientImpact, RegulatoryCategory, ImpactSeverity, ImpactStatus } from '@/types/client';

const categoryConfig: Record<RegulatoryCategory, { label: string; color: string }> = {
  tax: { label: 'Tax', color: 'text-blue-600' },
  gst: { label: 'GST', color: 'text-green-600' },
  smsf: { label: 'SMSF', color: 'text-purple-600' },
  ato: { label: 'ATO', color: 'text-red-600' },
  asic: { label: 'ASIC', color: 'text-orange-600' },
  corporate: { label: 'Corporate', color: 'text-indigo-600' },
  trust: { label: 'Trust', color: 'text-pink-600' },
};

const severityConfig: Record<ImpactSeverity, { label: string; className: string }> = {
  critical: { label: 'Critical', className: 'bg-red-500/10 text-red-600 border-red-500/30' },
  high: { label: 'High', className: 'bg-orange-500/10 text-orange-600 border-orange-500/30' },
  medium: { label: 'Medium', className: 'bg-amber-500/10 text-amber-600 border-amber-500/30' },
  low: { label: 'Low', className: 'bg-blue-500/10 text-blue-600 border-blue-500/30' },
};

const statusConfig: Record<ImpactStatus, { label: string; className: string; icon: React.ReactNode }> = {
  new: { label: 'New', className: 'bg-blue-500/10 text-blue-600 border-blue-500/30', icon: <Sparkles className="h-3 w-3" /> },
  reviewed: { label: 'Reviewed', className: 'bg-purple-500/10 text-purple-600 border-purple-500/30', icon: <Eye className="h-3 w-3" /> },
  actioned: { label: 'Actioned', className: 'bg-green-500/10 text-green-600 border-green-500/30', icon: <CheckCircle2 className="h-3 w-3" /> },
  dismissed: { label: 'Dismissed', className: 'bg-gray-500/10 text-gray-600 border-gray-500/30', icon: <XCircle className="h-3 w-3" /> },
};

export function RegulatoryImpactPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [categoryFilter, setCategoryFilter] = useState<RegulatoryCategory | 'all'>('all');
  const [severityFilter, setSeverityFilter] = useState<ImpactSeverity | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<ImpactStatus | 'all'>('all');
  const [selectedRegulation, setSelectedRegulation] = useState<RegulatoryChange | null>(null);
  const [selectedImpacts, setSelectedImpacts] = useState<ClientImpact[]>([]);
  const [showImpactsDialog, setShowImpactsDialog] = useState(false);

  // Fetch regulations
  const { data: regulationsData, isLoading: regulationsLoading } = useQuery({
    queryKey: ['regulatory-changes', categoryFilter, severityFilter],
    queryFn: () => clientsService.getRegulatoryChanges({
      category: categoryFilter !== 'all' ? categoryFilter : undefined,
      severity: severityFilter !== 'all' ? severityFilter : undefined,
    }),
  });

  // Fetch client impacts
  const { data: impactsData, isLoading: impactsLoading } = useQuery({
    queryKey: ['regulatory-impacts', statusFilter],
    queryFn: () => clientsService.getClientImpacts({
      status: statusFilter !== 'all' ? statusFilter : undefined,
    }),
  });

  const regulations = regulationsData?.regulations || [];
  const stats = regulationsData?.stats;
  const impacts = impactsData?.impacts || [];

  // Update impact status
  const updateStatusMutation = useMutation({
    mutationFn: ({ impactId, status }: { impactId: string; status: ImpactStatus }) =>
      clientsService.updateImpactStatus(impactId, status),
    onSuccess: () => {
      toast.success('Impact status updated');
      queryClient.invalidateQueries({ queryKey: ['regulatory-impacts'] });
      queryClient.invalidateQueries({ queryKey: ['regulatory-changes'] });
    },
  });

  const handleViewImpacts = (regulation: RegulatoryChange) => {
    setSelectedRegulation(regulation);
    const relatedImpacts = impacts.filter(i => i.regulationId === regulation.id);
    setSelectedImpacts(relatedImpacts);
    setShowImpactsDialog(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Regulatory Change Impact Analyzer</h1>
          <p className="text-muted-foreground">
            AI-powered analysis of regulatory changes and their impact across your client portfolio
          </p>
        </div>
        <Button variant="outline">
          <AlertTriangle className="mr-2 h-4 w-4" />
          Run Analysis
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold">{stats.totalRegulations}</p>
                <p className="text-xs text-muted-foreground mt-1">Regulations</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-blue-500/30">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.newThisMonth}</p>
                <p className="text-xs text-muted-foreground mt-1">New This Month</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold">{stats.totalAffectedClients}</p>
                <p className="text-xs text-muted-foreground mt-1">Affected Clients</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-red-500/30">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600">{stats.criticalImpacts}</p>
                <p className="text-xs text-muted-foreground mt-1">Critical</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-orange-500/30">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-600">{stats.highImpacts}</p>
                <p className="text-xs text-muted-foreground mt-1">High Priority</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-green-500/30">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.totalEstimatedRevenue)}</p>
                <p className="text-xs text-muted-foreground mt-1">Est. Revenue</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-amber-500/30">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-600">{stats.unreviewedImpacts}</p>
                <p className="text-xs text-muted-foreground mt-1">Unreviewed</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <Select value={categoryFilter} onValueChange={(v) => setCategoryFilter(v as RegulatoryCategory | 'all')}>
                <SelectTrigger>
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {Object.entries(categoryConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <Select value={severityFilter} onValueChange={(v) => setSeverityFilter(v as ImpactSeverity | 'all')}>
                <SelectTrigger>
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severities</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as ImpactStatus | 'all')}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="reviewed">Reviewed</SelectItem>
                  <SelectItem value="actioned">Actioned</SelectItem>
                  <SelectItem value="dismissed">Dismissed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Regulations List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Regulatory Changes</CardTitle>
          <CardDescription>{regulations.length} regulation{regulations.length !== 1 ? 's' : ''} found</CardDescription>
        </CardHeader>
        <CardContent>
          {regulationsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </div>
          ) : regulations.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="font-medium">No regulatory changes found</p>
              <p className="text-sm text-muted-foreground mt-1">
                Try adjusting your filters
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {regulations.map((regulation: RegulatoryChange) => (
                <div
                  key={regulation.id}
                  className="p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold">{regulation.title}</h3>
                        <Badge variant="outline" className={cn(categoryConfig[regulation.category].color)}>
                          {categoryConfig[regulation.category].label}
                        </Badge>
                        <Badge variant="outline" className={severityConfig[regulation.severity].className}>
                          {severityConfig[regulation.severity].label}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">{regulation.description}</p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Effective: {formatDate(regulation.effectiveDate)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {regulation.affectedClients} clients affected
                        </span>
                        <span className="flex items-center gap-1">
                          <DollarSign className="h-3 w-3" />
                          Est. {formatCurrency(regulation.estimatedRevenue)}
                        </span>
                        {regulation.sourceUrl && (
                          <a
                            href={regulation.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 hover:text-primary"
                          >
                            <ExternalLink className="h-3 w-3" />
                            {regulation.source}
                          </a>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewImpacts(regulation)}
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      View Impacts ({regulation.affectedClients})
                    </Button>
                  </div>

                  <Separator className="my-3" />

                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="font-medium mb-2">Key Changes:</p>
                      <ul className="space-y-1 text-muted-foreground">
                        {regulation.keyChanges.map((change, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-primary mt-0.5">•</span>
                            <span>{change}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium mb-2">Required Actions:</p>
                      <ul className="space-y-1 text-muted-foreground">
                        {regulation.requiredActions.map((action, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>{action}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Client Impacts Dialog */}
      <Dialog open={showImpactsDialog} onOpenChange={setShowImpactsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{selectedRegulation?.title}</DialogTitle>
            <DialogDescription>
              Impact analysis for {selectedImpacts.length} affected client{selectedImpacts.length !== 1 ? 's' : ''}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-3">
              {selectedImpacts.map((impact) => (
                <div
                  key={impact.clientId}
                  className="p-4 rounded-lg border bg-card"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4
                          className="font-medium cursor-pointer hover:text-primary"
                          onClick={() => {
                            navigate(`/panel/files/clients/${impact.clientId}`);
                            setShowImpactsDialog(false);
                          }}
                        >
                          {impact.clientName}
                        </h4>
                        <Badge variant="outline" className="text-xs">{impact.clientType}</Badge>
                        <Badge variant="outline" className={cn('text-xs', severityConfig[impact.severity].className)}>
                          {severityConfig[impact.severity].label}
                        </Badge>
                        <Badge variant="outline" className={cn('text-xs flex items-center gap-1', statusConfig[impact.status].className)}>
                          {statusConfig[impact.status].icon}
                          {statusConfig[impact.status].label}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{impact.reasoning}</p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {impact.estimatedHours}h
                        </span>
                        <span className="flex items-center gap-1">
                          <DollarSign className="h-3 w-3" />
                          {formatCurrency(impact.estimatedValue)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div>
                      <p className="font-medium mb-1">Affected Areas:</p>
                      <div className="flex flex-wrap gap-1">
                        {impact.affectedAreas.map((area, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">{area}</Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="font-medium mb-1">Recommended Actions:</p>
                      <ul className="space-y-1 text-muted-foreground">
                        {impact.recommendedActions.map((action, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-primary">•</span>
                            <span>{action}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
